import { AccountSelector, Input, RadioGroup } from '@root/components'
import {
  ACC_TYPES_PURCHASE_PAYMENT,
  ACC_TYPES_SALES_PAYMENT,
  Conditions,
  Fields,
  Operands,
  RuleCreationSource
} from '@root/constants'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useState } from 'react'
import {
  Controller,
  FieldErrors,
  FormProvider,
  get,
  Path,
  useForm,
  useWatch
} from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type { UseMutationResult } from '@tanstack/react-query'
import FiltersSelector from './FiltersSelector'
import { HttpError, IAccountingRule, IAccountingRuleBlueprintEntry } from '@root/query'
import { isVATObliged, useBusinessContext } from '@root/context'
import toast from 'react-hot-toast'
import { onCtrlEnter, setAPIErrors, trapSpacesForRequiredFields, type APIError } from '@root/utils'
import DocumentCreationSection, { AttachmentStep } from './DocumentCreation/DocumentCreationSection'
import { DEFAULT_ACCRUAL_DATE_MODE, isPurchaseInvoiceSource } from './accrualDateMode'
import FormAccordionSection from './FormAccordionSection'
import WorkflowMatchedCard from './WorkflowMatchedCard'
import { DisabledFieldset } from './AccountingRuleForm.styled'
import { isWorkflowOwnedRule } from './isWorkflowOwnedRule'
import {
  firstErrorPath,
  initialSectionOpen,
  openSectionsWithErrors,
  sectionErrorFlags,
  useAccountingRuleSections,
  type SectionId,
  type SectionOpen
} from './accountingRuleSections'
import { FaExclamationTriangle } from 'react-icons/fa'

export const EMPTY_BLUEPRINT_ENTRY: IAccountingRuleBlueprintEntry = {
  account_id: null,
  amount: 's',
  default_vat_code: null,
  default_vat_rate_label: ''
}

const _getDefaultValues = (rule?: Partial<IAccountingRule>) => {
  const isEdit = rule?.id != null
  if (rule) {
    return {
      name: rule?.name,
      enabled: isEdit ? rule.enabled : false,
      payment_account: rule?.payment_account,
      created_from: rule?.created_from,
      contact_name: rule?.contact_name,
      rule_node: rule?.rule_node,
      blueprint: rule?.blueprint,
      description: rule?.description,
      tag_ids: rule?.tag_ids || [],
      wait_for_attachment: rule?.wait_for_attachment ?? true,
      attachment_exempt: rule?.attachment_exempt ?? false,
      accrual_date_mode: rule?.accrual_date_mode ?? null
    }
  }

  return {
    name: '',
    enabled: false,
    payment_account: null,
    created_from: RuleCreationSource.USER,
    description: '',
    tag_ids: [],
    rule_node: {
      operand: Operands.AND,
      rules: [
        {
          field: Fields.CONTACT,
          condition: Conditions.CONTACT_IS_IN,
          value: []
        }
      ]
    },
    blueprint: {
      debet_entries: [EMPTY_BLUEPRINT_ENTRY],
      credit_entries: [EMPTY_BLUEPRINT_ENTRY]
    },
    wait_for_attachment: true,
    attachment_exempt: false,
    accrual_date_mode: null
  }
}

interface Props {
  rule?: Partial<IAccountingRule>
  saveMutation: UseMutationResult<IAccountingRule, HttpError<APIError>, Partial<IAccountingRule>>
}

const AccountingRuleForm: React.FC<Props> = ({ rule, saveMutation }) => {
  const [t] = useTranslation()
  const { data: business } = useBusinessContext()
  const vat_obliged = isVATObliged(business)
  const isWorkflowRule = isWorkflowOwnedRule(rule)
  const [open, setOpen] = useState<SectionOpen>(initialSectionOpen(rule != null))

  const methods = useForm<Partial<IAccountingRule>>({
    defaultValues: _getDefaultValues(rule)
  })

  const {
    register,
    control,
    handleSubmit,
    clearErrors,
    setError,
    setFocus,
    setValue,
    formState: { errors, submitCount }
  } = methods

  const accrualDateMode = useWatch({ control, name: 'accrual_date_mode' })
  const hasAccrual = Boolean(business?.has_accrual_based_entries_for_invoicing)
  const isPurchaseSource = isPurchaseInvoiceSource(hasAccrual, accrualDateMode)

  const revealInvalidSections = (formErrors: FieldErrors<Partial<IAccountingRule>>) => {
    setOpen(current => openSectionsWithErrors(current, sectionErrorFlags(formErrors)))
    const path = firstErrorPath(formErrors)
    if (!path) return
    requestAnimationFrame(() => {
      setFocus(path as Path<Partial<IAccountingRule>>)
    })
  }

  const handleMutationError = (error: HttpError<APIError>) => {
    const { status, data } = error
    if (status === 400 && data) {
      setOpen(current =>
        openSectionsWithErrors(current, {
          paymentAccount: 'payment_account' in data,
          ruleNode: 'rule_node' in data,
          booking: 'description' in data || 'blueprint' in data || 'accrual_date_mode' in data
        })
      )
    }
    if (status === 400 && data?.rule_node?.[0]?.type === 'too_short') {
      setError('rule_node', { type: 'too_short' })
      toast.error(t('settings.automation.accountingRules.form.ruleNodeEmptyError'))
    } else if (status === 400 && data) {
      setAPIErrors(data, setError)
      toast.error(t('general.formValidationError'))
    } else {
      toast.error(t('general.error'))
    }
  }

  const removeVatHandlingFromEntries = (entries: IAccountingRuleBlueprintEntry[]) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return entries.map(({ default_vat_code, default_vat_rate_label, ...rest }) => rest)
  }

  const onSubmit = (data: IAccountingRule) => {
    if (isWorkflowRule) return

    if (!vat_obliged) {
      data.blueprint.debet_entries = removeVatHandlingFromEntries(data.blueprint.debet_entries)
      data.blueprint.credit_entries = removeVatHandlingFromEntries(data.blueprint.credit_entries)
    }

    if (hasAccrual && data.accrual_date_mode) {
      data.payment_account = null
    } else {
      data.accrual_date_mode = null
    }

    saveMutation.mutate(data, { onError: handleMutationError })
  }

  const submit = handleSubmit(onSubmit, revealInvalidSections)
  const { summaries, complete } = useAccountingRuleSections(control, { isWorkflowRule })
  const errorFlags = sectionErrorFlags(errors)
  const fieldErrorCount = Object.keys(errors).length
  const ruleNodeError = errors.rule_node
  const onlyConditionsMissing =
    fieldErrorCount === 1 &&
    !!ruleNodeError &&
    typeof ruleNodeError === 'object' &&
    'type' in ruleNodeError &&
    ruleNodeError.type === 'too_short'
  const showErrorBanner = !isWorkflowRule && submitCount > 0 && fieldErrorCount > 0
  const allOpen = isWorkflowRule ? open[3] && open[4] : open[1] && open[2] && open[3] && open[4]

  const sectionHasError = (id: SectionId) => {
    if (isWorkflowRule || submitCount === 0) return false
    if (id === 1) return Boolean(errorFlags.paymentAccount)
    if (id === 2) return Boolean(errorFlags.ruleNode)
    if (id === 3) return Boolean(errorFlags.booking)
    return false
  }

  const toggleSection = (id: SectionId) => {
    setOpen(current => ({ ...current, [id]: !current[id] }))
  }

  return (
    <FormProvider {...methods}>
      <StyledForm
        id="automation-rule-form"
        onSubmit={submit}
        onKeyDown={e => {
          if (!isWorkflowRule) onCtrlEnter(e, submit)
        }}
      >
        {showErrorBanner && (
          <ErrorBanner>
            <FaExclamationTriangle />
            {t(
              onlyConditionsMissing
                ? 'settings.automation.accountingRules.form.errorBannerConditions'
                : 'settings.automation.accountingRules.form.errorBannerFields'
            )}
          </ErrorBanner>
        )}

        <Input
          required={!isWorkflowRule}
          label={t('settings.automation.accountingRules.form.name')}
          info={t('settings.automation.accountingRules.form.nameInfo')}
          autoFocus={!isWorkflowRule}
          autoComplete="off"
          disabled={isWorkflowRule}
          {...register('name', {
            required: isWorkflowRule ? false : { value: true, message: t('validation.required') },
            validate: isWorkflowRule
              ? undefined
              : value => trapSpacesForRequiredFields(value, t('validation.required'))
          })}
          errors={get(errors, 'name')}
        />

        <SectionStack>
          <ToggleRow>
            <ToggleAllButton
              type="button"
              onClick={() => {
                const next = !allOpen
                if (isWorkflowRule) {
                  setOpen(current => ({ ...current, 3: next, 4: next }))
                } else {
                  setOpen({ 1: next, 2: next, 3: next, 4: next })
                }
              }}
            >
              {t(
                allOpen
                  ? 'settings.automation.accountingRules.form.collapseAll'
                  : 'settings.automation.accountingRules.form.expandAll'
              )}
            </ToggleAllButton>
          </ToggleRow>

          {isWorkflowRule && typeof rule?.workflow === 'number' && (
            <WorkflowMatchedCard workflowId={rule.workflow} nodeUuid={rule.workflow_node_uuid} />
          )}

          {!isWorkflowRule && (
            <>
              <FormAccordionSection
                sectionId={1}
                title={t(
                  isPurchaseSource
                    ? 'settings.automation.accountingRules.form.step1TitleAccrual'
                    : 'settings.automation.accountingRules.form.step1Title'
                )}
                subtitle={t(
                  isPurchaseSource
                    ? 'settings.automation.accountingRules.form.step1SubtitleAccrual'
                    : 'settings.automation.accountingRules.form.step1Subtitle'
                )}
                summary={summaries[1]}
                open={open[1]}
                complete={complete[1]}
                hasError={sectionHasError(1)}
                onToggle={() => toggleSection(1)}
              >
                <SourceFields>
                  {hasAccrual && (
                    <RadioGroup
                      name="rule_source"
                      value={isPurchaseSource ? 'purchase' : 'bank'}
                      onChange={next => {
                        if (next === 'purchase') {
                          clearErrors('payment_account')
                          setValue(
                            'accrual_date_mode',
                            accrualDateMode || DEFAULT_ACCRUAL_DATE_MODE,
                            {
                              shouldDirty: true,
                              shouldValidate: submitCount > 0
                            }
                          )
                          return
                        }
                        setValue('accrual_date_mode', null, {
                          shouldDirty: true,
                          shouldValidate: submitCount > 0
                        })
                      }}
                      options={[
                        {
                          value: 'bank',
                          label: t('settings.automation.accountingRules.form.sourceBank'),
                          description: t(
                            'settings.automation.accountingRules.form.sourceBankDescription'
                          )
                        },
                        {
                          value: 'purchase',
                          label: t('settings.automation.accountingRules.form.sourcePurchase'),
                          description: t(
                            'settings.automation.accountingRules.form.sourcePurchaseDescription'
                          )
                        }
                      ]}
                    />
                  )}
                  <AnimatePresence initial={false}>
                    {!isPurchaseSource && (
                      <AccountReveal
                        key="payment-account"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                      >
                        <AccountInner $spaced={hasAccrual}>
                          <Controller
                            control={control}
                            rules={{
                              required: { value: true, message: t('validation.required') }
                            }}
                            name={'payment_account'}
                            render={({ field: { value, onChange }, fieldState: { error } }) => (
                              <AccountSelector
                                id={'automation-rule-account-select'}
                                label={t('settings.automation.accountingRules.form.account')}
                                required={true}
                                accountFilter={{
                                  type: [...ACC_TYPES_PURCHASE_PAYMENT, ...ACC_TYPES_SALES_PAYMENT]
                                }}
                                errors={error}
                                value={value}
                                onChange={onChange}
                              />
                            )}
                          />
                        </AccountInner>
                      </AccountReveal>
                    )}
                  </AnimatePresence>
                </SourceFields>
              </FormAccordionSection>

              <FormAccordionSection
                sectionId={2}
                title={t('settings.automation.accountingRules.form.step2Title')}
                subtitle={
                  <Trans
                    i18nKey="settings.automation.accountingRules.form.step2Subtitle"
                    components={{ strong: <strong /> }}
                  />
                }
                summary={summaries[2]}
                open={open[2]}
                complete={complete[2]}
                hasError={sectionHasError(2)}
                onToggle={() => toggleSection(2)}
              >
                <FiltersSelector isBlank={rule == null} />
              </FormAccordionSection>
            </>
          )}

          <FormAccordionSection
            sectionId={3}
            displayNumber={isWorkflowRule ? 2 : undefined}
            title={t('settings.automation.accountingRules.form.step3Title')}
            subtitle={t('settings.automation.accountingRules.form.step3Subtitle')}
            summary={summaries[3]}
            open={open[3]}
            complete={complete[3]}
            hasError={sectionHasError(3)}
            onToggle={() => toggleSection(3)}
          >
            <DisabledFieldset disabled={isWorkflowRule}>
              <DocumentCreationSection
                showWorkflowContact={isWorkflowRule}
                readOnly={isWorkflowRule}
              />
            </DisabledFieldset>
          </FormAccordionSection>

          <FormAccordionSection
            sectionId={4}
            displayNumber={isWorkflowRule ? 3 : undefined}
            title={t('settings.automation.accountingRules.form.step4Title')}
            subtitle={t('settings.automation.accountingRules.form.step4Subtitle')}
            summary={summaries[4]}
            open={open[4]}
            complete={complete[4]}
            hasError={sectionHasError(4)}
            onToggle={() => toggleSection(4)}
          >
            <DisabledFieldset disabled={isWorkflowRule}>
              <AttachmentStep readOnly={isWorkflowRule} />
            </DisabledFieldset>
          </FormAccordionSection>
        </SectionStack>
      </StyledForm>
    </FormProvider>
  )
}

export default AccountingRuleForm

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
`

const SectionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const ToggleRow = styled.div`
  display: flex;
  justify-content: flex-end;
`

const ToggleAllButton = styled.button`
  margin-bottom: -${({ theme }) => theme.spacing.xs}rem;
  background: transparent;
  border: 0;
  color: ${({ theme }) => theme.dashboard.infoDeep};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  padding: 0.2rem ${({ theme }) => theme.spacing.xs}rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;

  &:hover {
    background: ${({ theme }) => theme.dashboard.infoTint};
  }
`

const SourceFields = styled.div`
  display: flex;
  flex-direction: column;
`

const AccountReveal = styled(motion.div)`
  overflow: hidden;
`

const AccountInner = styled.div<{ $spaced: boolean }>`
  padding-top: ${({ $spaced, theme }) => ($spaced ? theme.spacing.sm : 0)}rem;
`

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  border: 2px solid ${({ theme }) => theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
  background: ${({ theme }) => theme.dashboard.criticalTint};
  color: ${({ theme }) => theme.dashboard.critical};
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
`
